import React, { useEffect, useState } from "react";
import { FlaskConical, Paperclip, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Switch } from "../ui/switch";
import AutomationEditor from "./AutomationEditor";

const EMPTY = { name: "Nouvelle automatisation", tab: "demarches", status: "", email_on: true, template_id: "", to_partner: true, to_client: false, to_fixed: [], attachments: [] };

function AutoRow({ a, meta, onEdit, onToggle, onDelete }) {
  const tab = meta.tabs.find((t) => t.key === a.tab);
  const st = tab?.statuses.find((s) => s.id === a.status)?.label || a.status;
  const tname = (id) => meta.templates.find((t) => t.id === id)?.name || "(modèle supprimé)";
  const to = [a.to_partner && "partenaire de l'onglet", a.to_client && "client", ...(a.to_fixed || []), ...(a.wa_fixed || [])].filter(Boolean).join(", ");
  const sTab = meta.tabs.find((t) => t.key === a.status_tab);
  const actions = [a.email_on && `email « ${tname(a.template_id)} »`, a.wa_on && `WhatsApp « ${tname(a.wa_template_id)} »`, a.task_on && `tâche « ${a.task_title} »`,
    a.status_on && `${sTab?.label || a.status_tab} → ${sTab?.statuses.find((s) => s.id === a.status_target)?.label || a.status_target}`].filter(Boolean);
  return (
    <div className="flex items-center gap-4 px-4 py-3 border border-norea-border rounded-md bg-white hover:border-norea-primary/40 transition-colors" data-testid={`auto-row-${a.id}`}>
      <Switch checked={a.active} onCheckedChange={(v) => onToggle(a, v)} data-testid={`auto-toggle-${a.id}`} />
      <button onClick={onEdit} className="flex-1 min-w-0 text-left" data-testid={`auto-edit-${a.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-norea-text">{a.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${a.active ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-norea-border text-norea-textMuted"}`} data-testid={`auto-state-${a.id}`}>{a.active ? "Active" : "Désactivée"}</span>
          {a.example && <span className="text-[10px] px-1.5 py-0.5 rounded border border-norea-border text-norea-textMuted">Exemple</span>}
        </div>
        <div className="text-[11px] text-norea-textMuted mt-0.5">
          Quand <strong>{tab?.label || a.tab}</strong> passe à <strong>{st}</strong> → <span data-testid={`auto-actions-${a.id}`}>{actions.join(" · ") || "aucune action"}</span>{(a.email_on || a.wa_on) && ` (à ${to || "—"})`}
          {a.attachments?.length > 0 && <span className="inline-flex items-center gap-0.5 ml-2"><Paperclip className="w-3 h-3" />{a.attachments.length}</span>}
        </div>
        <div className="text-[10px] text-norea-textDisabled mt-0.5" data-testid={`auto-runs-${a.id}`}>{a.runs.pending} à approuver · {a.runs.approved} approuvé(s) · {a.runs.rejected} refusé(s)</div>
      </button>
      <button onClick={onDelete} className="p-1.5 text-norea-textMuted hover:text-status-erreurText" data-testid={`auto-delete-${a.id}`} aria-label="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function AutomationsPanel() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/automations").then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);
  const toggle = async (a, active) => {
    try { await api.post(`/automations/${a.id}/toggle`, { active }); toast.success(active ? "Automatisation activée" : "Automatisation désactivée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'automatisation « ${a.name} » ?`)) return;
    try { await api.delete(`/automations/${a.id}`); toast.success("Supprimée"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  if (!data) return <div className="h-40 bg-white border border-norea-border rounded-md animate-pulse" />;
  if (editing) return <AutomationEditor automation={editing} meta={data} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  return (
    <div className="space-y-4" data-testid="automations-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-norea-text inline-flex items-center gap-2"><Zap className="w-4 h-4 text-norea-primary" />Automatisations au changement de statut</h2>
          <p className="text-xs text-norea-textMuted mt-1 max-w-2xl">Quand le statut d'un onglet change : email et WhatsApp préparés depuis vos modèles (avec pièces), changement de statut d'un autre onglet — tout attend dans « À approuver » — et tâche créée directement. Rien ne part sans validation. Toutes les automatisations sont désactivées par défaut.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary text-xs shrink-0" data-testid="auto-new"><Plus className="w-3.5 h-3.5" /> Nouvelle automatisation</button>
      </div>
      {data.simulation && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="auto-simulation-banner">
          <FlaskConical className="w-3.5 h-3.5" />Mode simulation : un email approuvé est noté « envoyé (simulation) » dans le fil de l'onglet, aucun email ne part réellement.
        </div>
      )}
      <div className="space-y-2" data-testid="auto-list">
        {data.items.map((a) => <AutoRow key={a.id} a={a} meta={data} onEdit={() => setEditing(a)} onToggle={toggle} onDelete={() => remove(a)} />)}
        {!data.items.length && <div className="py-6 text-center text-sm italic text-norea-textMuted">Aucune automatisation.</div>}
      </div>
    </div>
  );
}
